import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Therapist, Student, Schedule, Assignment } from '../types';

// State interface
interface AppState {
  therapists: Therapist[];
  students: Student[];
  schedules: Schedule[];
  assignments: Assignment[];
  loading: boolean;
  error: string | null;
  selectedSchedule: Schedule | null;
  selectedDateRange: {
    startDate: Date;
    endDate: Date;
  };
}

// Action types
type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_THERAPISTS'; payload: Therapist[] }
  | { type: 'SET_STUDENTS'; payload: Student[] }
  | { type: 'SET_SCHEDULES'; payload: Schedule[] }
  | { type: 'SET_ASSIGNMENTS'; payload: Assignment[] }
  | { type: 'SET_SELECTED_SCHEDULE'; payload: Schedule | null }
  | { type: 'SET_DATE_RANGE'; payload: { startDate: Date; endDate: Date } }
  | { type: 'ADD_THERAPIST'; payload: Therapist }
  | { type: 'UPDATE_THERAPIST'; payload: Therapist }
  | { type: 'REMOVE_THERAPIST'; payload: number }
  | { type: 'ADD_STUDENT'; payload: Student }
  | { type: 'UPDATE_STUDENT'; payload: Student }
  | { type: 'REMOVE_STUDENT'; payload: number }
  | { type: 'ADD_ASSIGNMENT'; payload: Assignment }
  | { type: 'UPDATE_ASSIGNMENT'; payload: Assignment }
  | { type: 'REMOVE_ASSIGNMENT'; payload: number };

// Initial state
const initialState: AppState = {
  therapists: [],
  students: [],
  schedules: [],
  assignments: [],
  loading: false,
  error: null,
  selectedSchedule: null,
  selectedDateRange: {
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Next week
  }
};

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_THERAPISTS':
      return { ...state, therapists: action.payload };
    
    case 'SET_STUDENTS':
      return { ...state, students: action.payload };
    
    case 'SET_SCHEDULES':
      return { ...state, schedules: action.payload };
    
    case 'SET_ASSIGNMENTS':
      return { ...state, assignments: action.payload };
    
    case 'SET_SELECTED_SCHEDULE':
      return { ...state, selectedSchedule: action.payload };
    
    case 'SET_DATE_RANGE':
      return { ...state, selectedDateRange: action.payload };
    
    case 'ADD_THERAPIST':
      return { ...state, therapists: [...state.therapists, action.payload] };
    
    case 'UPDATE_THERAPIST':
      return {
        ...state,
        therapists: state.therapists.map(t => 
          t.id === action.payload.id ? action.payload : t
        )
      };
    
    case 'REMOVE_THERAPIST':
      return {
        ...state,
        therapists: state.therapists.filter(t => t.id !== action.payload)
      };
    
    case 'ADD_STUDENT':
      return { ...state, students: [...state.students, action.payload] };
    
    case 'UPDATE_STUDENT':
      return {
        ...state,
        students: state.students.map(s => 
          s.id === action.payload.id ? action.payload : s
        )
      };
    
    case 'REMOVE_STUDENT':
      return {
        ...state,
        students: state.students.filter(s => s.id !== action.payload)
      };
    
    case 'ADD_ASSIGNMENT':
      return { ...state, assignments: [...state.assignments, action.payload] };
    
    case 'UPDATE_ASSIGNMENT':
      return {
        ...state,
        assignments: state.assignments.map(a => 
          a.id === action.payload.id ? action.payload : a
        )
      };
    
    case 'REMOVE_ASSIGNMENT':
      return {
        ...state,
        assignments: state.assignments.filter(a => a.id !== action.payload)
      };
    
    default:
      return state;
  }
};

// Context
const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;